/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type monaco from 'monaco-editor';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import Retracked from 'js/lib/retracked';

import type { Hint } from 'bundles/cml/legacy/types/Hint';
import CodeEvaluatorOutput from 'bundles/code-evaluator/components/CodeEvaluatorOutput';
import type Evaluation from 'bundles/code-evaluator/models/Evaluation';
import EvaluationRunner from 'bundles/code-evaluator/models/EvaluationRunner';
import type { EvaluatorSummary } from 'bundles/code-evaluator/models/EvaluatorSummary';
import EvaluatorHintUtils from 'bundles/code-evaluator/utils/EvaluatorHintUtils';
import Notification from 'bundles/coursera-ui/components/extended/Notification';
import TrackedButton from 'bundles/page/components/TrackedButton';

import _t from 'i18n!nls/cml';

export type Monaco = {
  Range: typeof monaco.Range;
};

type Props = {
  monacoEditor: monaco.editor.IStandaloneCodeEditor;
  monacoInstance: Monaco;
  evaluatorId: string;
  starterExpression: string;
  onUpdate?: (answer: string) => void;
  evaluatorSummary: EvaluatorSummary;
};

type State = {
  value: string;
  evaluation: InstanceType<typeof Evaluation>;
  evaluationRunner: EvaluationRunner;
  hints: Hint[];
  showHintMessage: boolean;
};

const styles = {
  root: css`
    .hintAlert {
      left: 7px !important; /* override monaco in-line style rule */
      background: url("assets + 'images/teach-course/triangle_alert.svg'") no-repeat;
    }

    .hintHighlight {
      background-color: rgb(245 166 35 / 35%);
    }

    button.cml-code-evaluator-run {
      padding: 0;
      display: flex;
      justify-content: center;

      span {
        padding: 10px 20px;
      }
    }

    button.cml-code-evaluator-reset {
      padding: 0;

      span {
        padding: 0 20px;
      }
    }
  `,
};

/**
 * @class CMLCodeEvaluatorV2
 *
 * CMLCodeEvaluator based on monaco editor.
 *
 */
class CMLCodeEvaluatorV2 extends React.Component<Props, State> {
  private declare buttonRef: HTMLButtonElement | null;

  constructor(props: Props) {
    super(props);

    this.buttonRef = null;
    const { monacoEditor, evaluatorId } = props;
    const value = monacoEditor.getValue();
    const evaluationRunner = new EvaluationRunner(evaluatorId);
    const { evaluation } = evaluationRunner;
    this.state = {
      value,
      evaluation,
      evaluationRunner,
      hints: [],
      showHintMessage: true,
    };

    monacoEditor.onDidChangeModelContent(this.handleChange);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  handleChange = (event: monaco.editor.IModelContentChangedEvent) => {
    const {
      changes: [currentChange],
      isFlush,
    } = event;
    const { monacoEditor, onUpdate } = this.props;
    const { hints } = this.state;

    const changeDelta = { currentChange, isFlush };
    const model = monacoEditor.getModel();
    const { startColumn, startLineNumber } = currentChange.range;
    const changeStartPosition = model?.getOffsetAt({ column: startColumn, lineNumber: startLineNumber }) ?? 0;
    const updatedHints = EvaluatorHintUtils.getUpdatedHints(hints, changeStartPosition, changeDelta);

    const updatedValue = monacoEditor.getValue();
    this.setState({ value: updatedValue, hints: updatedHints });
    if (onUpdate) {
      onUpdate(updatedValue);
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    const { keyCode } = event;
    const target = event.target as HTMLTextAreaElement;
    // Press Escape key inside code editor to exit and pass focus to "Run" button
    if (keyCode === 27 && target.className === 'monaco-editor') {
      this.handleFocus();
    }
  };

  handleReset = () => {
    const { monacoEditor, starterExpression, evaluatorId } = this.props;
    monacoEditor.setValue(starterExpression);

    const evaluationRunner = new EvaluationRunner(evaluatorId);
    const { evaluation } = evaluationRunner;

    this.setState({
      hints: [],
      evaluation,
      evaluationRunner,
    });
  };

  handleRun = () => {
    const { value, evaluationRunner } = this.state;
    evaluationRunner.run(value, (evaluation: Evaluation) => {
      const hints = EvaluatorHintUtils.initHints(evaluation);
      this.setState({ evaluation, hints });
    });
  };

  handleFocus = () => {
    const { buttonRef } = this;
    if (buttonRef) {
      buttonRef.focus();
    }
  };

  handleCancel = () => {
    const { evaluationRunner } = this.state;
    evaluationRunner.cancel((evaluation: Evaluation) => {
      this.setState({ evaluation, hints: [] });
    });
  };

  handleDismissHintMessage = () => {
    this.setState({ showHintMessage: false });
  };

  assignRef = (node: HTMLButtonElement | null) => {
    this.buttonRef = node;
  };

  nextHintIndex = () => {
    const { hints } = this.state;
    return hints.findIndex((hint) => !hint.show);
  };

  showNextHint = () => {
    const { hints } = this.state;

    const hintIndex = this.nextHintIndex();
    if (hintIndex >= 0) {
      const newHints = hints.slice();
      newHints[hintIndex].show = true;
      this.setState({ hints: newHints });
    }
  };

  renderHints = () => {
    const { monacoEditor, monacoInstance } = this.props;
    const { hints } = this.state;
    const model = monacoEditor.getModel();
    if (!model || hints.length <= 0) {
      return;
    }

    const displayedHints = hints.filter((hint) => hint.show);
    const glyphs = displayedHints.map((displayedHint) => {
      const { lineNumber: startLineNumber, column: startColumn } = model.getPositionAt(displayedHint.startPosition);
      const { lineNumber: endLineNumber, column: endColumn } = model.getPositionAt(displayedHint.endPosition);
      return {
        range: new monacoInstance.Range(startLineNumber, startColumn, endLineNumber, endColumn),
        options: {
          glyphMarginHoverMessage: { value: displayedHint.text },
          hoverMessage: { value: displayedHint.text },
          className: 'hintHighlight',
          glyphMarginClassName: 'hintAlert',
        },
      };
    });
    monacoEditor.deltaDecorations([], [...glyphs]);
  };

  render() {
    const { evaluatorSummary, evaluatorId } = this.props;
    const {
      hints,
      showHintMessage,
      evaluation,
      evaluation: { active },
    } = this.state;

    const moreHintsAvailable = this.nextHintIndex() >= 0;
    this.renderHints();
    const displayedHints = hints.filter((hint) => hint.show);
    const hintNotification = (
      <Notification
        type="info"
        isDismissible={true}
        onDismiss={this.handleDismissHintMessage}
        message={
          <FormattedHTMLMessage
            message={
              '<strong>Hint:</strong> Hover over a marker in the margin to see a hint about \nhow to correct your submission.'
            }
          />
        }
      />
    );

    return (
      <div className="rc-CMLCodeEvaluator" css={styles.root}>
        {moreHintsAvailable && (
          <TrackedButton
            trackingName="show_hint_button"
            data={{ evaluatorId }}
            className="secondary cml-code-evaluator-hint read-only-wrapper-allow-interaction"
            onClick={this.showNextHint}
            withVisibilityTracking={true}
          >
            {/* we need to wrap the content in spans so that the read-only-wrapper-allow-interaction class is able to work because it requires having a child element that is able to receive the event and bubble it up */}
            <span>{_t('Show Hint')}</span>
          </TrackedButton>
        )}
        <button
          disabled={active}
          className="secondary cml-code-evaluator-run read-only-wrapper-allow-interaction"
          onClick={this.handleRun}
          ref={this.assignRef}
          type="button"
        >
          <span>{_t('Run')}</span>
        </button>

        <button
          disabled={active}
          type="button"
          className="button-link cml-code-evaluator-reset read-only-wrapper-allow-interaction"
          onClick={this.handleReset}
        >
          <span>{_t('Reset')}</span>
        </button>

        {displayedHints.length > 0 && showHintMessage && hintNotification}

        <CodeEvaluatorOutput
          evaluatorId={evaluatorId}
          evaluation={evaluation}
          evaluatorSummary={evaluatorSummary}
          onCancel={this.handleCancel}
        />
      </div>
    );
  }
}

export default Retracked.createTrackedContainer<Props>(() => {
  return {
    namespace: {
      app: 'cml_code_evaluator',
      page: 'unknown',
    },
  };
})(CMLCodeEvaluatorV2);
